import {
  CREATE_CATALOG_TYPE,
  DELETE_CATALOG_TYPE,
  FETCH_CATALOG_TYPE_BY_ID,
  SEARCH_CATALOG_TYPES,
  UPDATE_CATALOG_TYPE,
} from "@apiUtils";
import {
  DELETE_FORM,
  FORM_BUILDERS_KEY,
  getFormBuilderByIdKey,
  mutationFetch,
  type SearchPayload,
  type UseSearchQueryOptions,
  useSearchQuery,
} from "@atlas/sdk";
import type { IHubCatalogType } from "@models";
import {
  type UseMutationOptions,
  type UseQueryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import type { AxiosError } from "axios";
import { HUB_CATALOGS_BY_TYPE_KEY, HUB_CATALOGS_KEY } from "./HubCatalogs";

export const HUB_CATALOG_TYPES_KEY = "hubCatalogTypesKey";
export const HUB_CATALOG_TYPE_BY_ID_KEY = "hubCatalogTypeByIdKey";

/** Default sort for catalog type list/dropdowns (typeId ascending). */
export const HUB_CATALOG_TYPES_DEFAULT_SORT = ["typeId ASC"] as const;

export const getHubCatalogTypesKey = () => [HUB_CATALOG_TYPES_KEY] as const;

export const getHubCatalogTypeByIdKey = (typeId: number) =>
  [HUB_CATALOG_TYPE_BY_ID_KEY, typeId] as const;

export const useSearchHubCatalogTypes = (
  payload: SearchPayload,
  options?: Omit<UseSearchQueryOptions<IHubCatalogType>, "url" | "payload">,
) => {
  return useSearchQuery<IHubCatalogType>({
    url: SEARCH_CATALOG_TYPES,
    payload,
    ...options,
  });
};

/** Catalog types as a flat list (for dropdowns / lookups). */
export const useHubCatalogTypes = (
  payload: SearchPayload,
  options?: Omit<
    UseSearchQueryOptions<IHubCatalogType>,
    "url" | "payload" | "select"
  >,
) => {
  const query = useSearchHubCatalogTypes(payload, options);

  return {
    ...query,
    data: query.data?.pages.flatMap((page) => page.items ?? []),
  };
};

export const useHubCatalogType = (
  typeId: number,
  options?: Partial<
    UseQueryOptions<
      IHubCatalogType,
      AxiosError,
      IHubCatalogType,
      ReturnType<typeof getHubCatalogTypeByIdKey>
    >
  >,
) => {
  return useQuery({
    queryKey: getHubCatalogTypeByIdKey(typeId),
    queryFn: async () => {
      return mutationFetch<IHubCatalogType>({
        url: FETCH_CATALOG_TYPE_BY_ID.replace(":typeId", String(typeId)),
        method: "GET",
      });
    },
    ...options,
    enabled: (options?.enabled ?? true) && typeId > 0,
  });
};

const createHubCatalogType = (
  body: IHubCatalogType,
): Promise<IHubCatalogType> => {
  return mutationFetch<IHubCatalogType>({
    url: CREATE_CATALOG_TYPE,
    method: "POST",
    body,
  });
};

const updateHubCatalogType = (
  body: IHubCatalogType,
): Promise<IHubCatalogType> => {
  const typeId = body.typeId;
  if (typeId == null) {
    throw new Error("typeId is required for update");
  }
  return mutationFetch<IHubCatalogType>({
    url: UPDATE_CATALOG_TYPE.replace(":typeId", String(typeId)),
    method: "PUT",
    body,
  });
};

const deleteHubCatalogType = async (
  catalogType: IHubCatalogType,
): Promise<void> => {
  const typeId = catalogType.typeId;
  if (typeId == null) {
    throw new Error("typeId is required for delete");
  }

  await mutationFetch<void>({
    url: DELETE_CATALOG_TYPE.replace(":typeId", String(typeId)),
    method: "DELETE",
  });

  const formId = catalogType.formId ?? 0;
  if (formId > 0) {
    await mutationFetch<void>({
      url: DELETE_FORM.replace(":formId", String(formId)),
      method: "DELETE",
    });
  }
};

export const useCreateHubCatalogType = (
  options?: UseMutationOptions<IHubCatalogType, AxiosError, IHubCatalogType>,
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createHubCatalogType,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [HUB_CATALOG_TYPES_KEY] });
      queryClient.invalidateQueries({ queryKey: [SEARCH_CATALOG_TYPES] });
      queryClient.invalidateQueries({ queryKey: [HUB_CATALOGS_KEY] });
      queryClient.invalidateQueries({ queryKey: [HUB_CATALOGS_BY_TYPE_KEY] });
    },
    ...options,
  });
};

export const useUpdateHubCatalogType = (
  options?: UseMutationOptions<IHubCatalogType, AxiosError, IHubCatalogType>,
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateHubCatalogType,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: [HUB_CATALOG_TYPES_KEY] });
      queryClient.invalidateQueries({ queryKey: [SEARCH_CATALOG_TYPES] });
      queryClient.invalidateQueries({ queryKey: [HUB_CATALOGS_KEY] });
      queryClient.invalidateQueries({ queryKey: [HUB_CATALOGS_BY_TYPE_KEY] });
      if (data.typeId != null) {
        queryClient.invalidateQueries({
          queryKey: getHubCatalogTypeByIdKey(data.typeId),
        });
      }
    },
    ...options,
  });
};

export const useDeleteHubCatalogType = (
  options?: UseMutationOptions<void, AxiosError, IHubCatalogType>,
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteHubCatalogType,
    onSuccess: (_, catalogType) => {
      queryClient.invalidateQueries({ queryKey: [HUB_CATALOG_TYPES_KEY] });
      queryClient.invalidateQueries({ queryKey: [SEARCH_CATALOG_TYPES] });
      if (catalogType.typeId != null) {
        queryClient.invalidateQueries({
          queryKey: getHubCatalogTypeByIdKey(catalogType.typeId),
        });
      }
      const formId = catalogType.formId ?? 0;
      if (formId > 0) {
        queryClient.invalidateQueries({ queryKey: [FORM_BUILDERS_KEY] });
        queryClient.invalidateQueries({
          queryKey: getFormBuilderByIdKey(formId),
        });
      }
      queryClient.invalidateQueries({ queryKey: [HUB_CATALOGS_KEY] });
      queryClient.invalidateQueries({ queryKey: [HUB_CATALOGS_BY_TYPE_KEY] });
    },
    ...options,
  });
};
